import {
  ChainType,
  type ChainData,
  type CosmosChain,
  type Token,
} from "@0xsquid/squid-types";
import { StargateClient } from "@cosmjs/stargate";
import {
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { Interface, JsonRpcProvider, ethers } from "ethers";
import { erc20Abi } from "viem";
import { getClient } from "../../core/client";
import {
  SOLANA_RPC_URL,
  nativeEvmTokenAddress,
  nativeSolanaTokenAddress,
} from "../../core/constants";
import { getMulticall3Address, multicallAbi } from "../../core/multicall3";
import { CantonConnector } from "../../core/types/canton";
import { StellarToken } from "../../core/types/stellar";
import type { TokenWithBalance } from "../../core/types/tokens";
import { normalizeIbcAddress } from "../internal/assetsService";
import { isBitcoinAddressValid } from "../internal/bitcoinService";
import {
  buildHoldingsAcsRequestBody,
  cantonInstrumentKey,
  groupCantonHoldingBalances,
  parseCantonInstrument,
} from "../internal/cantonService";
import { formatBNToReadable, parseToBigInt } from "../internal/numberService";
import { isStellarToken } from "../internal/stellarService";
import { CosmosRestClient } from "./cosmosRestClient";
import { SuiRpcClient } from "./suiRpcClient";

type ContractAddress = `0x${string}`;
type SolanaParsedTokenAccount = {
  account?: {
    data?: {
      parsed?: {
        info?: {
          mint?: string;
          tokenAmount?: {
            amount?: string;
          };
        };
      };
    };
  };
};

const isTokenNative = (token: Token): boolean =>
  token.address.toLowerCase() === nativeEvmTokenAddress.toLowerCase();

const getTokenAddressForMultiCall = (t: Token): ContractAddress => {
  return isTokenNative(t)
    ? (getMulticall3Address(t.chainId) as ContractAddress)
    : (t.address as ContractAddress);
};

const getTokenBalanceCall = (token: Token, userAddress: string): string[] => {
  const address = getTokenAddressForMultiCall(token);
  const abi = isTokenNative(token) ? multicallAbi : erc20Abi;
  const method = isTokenNative(token) ? "getEthBalance" : "balanceOf";

  const iface = new Interface(abi);
  const callData = iface.encodeFunctionData(method, [userAddress]);
  return [address, callData];
};

/**
 * Using Multicall3 contract, available on a lot of chains
 * @param multicall
 * @param calls
 * @param tokens
 * @returns
 */
const executeMulticall = async (
  multicall: ethers.Contract,
  calls: string[][],
  tokens: Token[]
): Promise<
  { token: Token; result: { success: boolean; returnData: string } }[]
> => {
  try {
    const results = await multicall.tryAggregate.staticCall(false, calls);

    const tokenWithResult = tokens.map((token, index) => ({
      token,
      result: { success: results[index][0], returnData: results[index][1] },
    }));

    return tokenWithResult;
  } catch (error) {
    return tokens.map((token) => ({
      token,
      result: { success: false, returnData: "" },
    }));
  }
};

/**
 * Format balance using ethers, based on success of the call
 * If the call fails, we return 0 as balance
 * @param results
 * @returns
 */
const formatBalanceForTokens = (
  results: { token: Token; result: { success: boolean; returnData: string } }[]
): TokenWithBalance[] => {
  return results.map((data) => {
    const token = data.token;
    const result = data.result;
    if (!result.success) {
      return { ...token, balance: "0" };
    }
    try {
      return {
        ...token,
        balance: formatBNToReadable(result.returnData, token.decimals),
      };
    } catch {
      return {
        ...token,
        balance: "0",
      };
    }
  });
};

export const getTokensSupportingMultiCall = async (
  tokens: Token[],
  userAddress: string,
  chains: ChainData[]
): Promise<TokenWithBalance[]> => {
  // Create providers for each chain
  const providers: { [chainId: string]: JsonRpcProvider } = {};
  chains.forEach((chain) => {
    const provider = new JsonRpcProvider(chain.rpc);

    // Destroy the provider after a decent timeout
    // Given how JsonRpcProvider is implemented, this is the only way
    // to stop requests in case the URL is not functional
    // https://github.com/ethers-io/ethers.js/blob/9fd9d41d017a5e3b329aca47c79786e69cd40b99/src.ts/providers/provider-jsonrpc.ts#L770-L779
    setTimeout(() => {
      provider.destroy();
    }, 10_000);

    providers[chain.chainId] = provider;
  });

  const fetchBalancesForChain = async (chain: ChainData) => {
    const tokensForChain = tokens.filter((t) => t.chainId === chain.chainId);
    if (tokensForChain.length === 0) return [];

    const provider = providers[chain.chainId];
    if (!provider) {
      return [
        ...tokensForChain.map((t) => ({
          ...t,
          balance: "0",
        })),
      ];
    }

    const multicallAddress = getMulticall3Address(chain.chainId);
    const multicall = new ethers.Contract(
      multicallAddress,
      multicallAbi,
      provider
    );

    const calls = tokensForChain.map((token) =>
      getTokenBalanceCall(token, userAddress)
    );

    const multicallResults = await executeMulticall(
      multicall,
      calls,
      tokensForChain
    );

    return formatBalanceForTokens(multicallResults);
  };

  const allResults = await Promise.all(
    chains.map((chain) => fetchBalancesForChain(chain))
  );
  return allResults.flat();
};

export const getAllEvmTokensBalance = async (
  evmTokens: Token[],
  userAddress: string,
  chains: ChainData[]
): Promise<TokenWithBalance[]> => {
  const tokensMulticall = await getTokensSupportingMultiCall(
    evmTokens,
    userAddress,
    chains
  );

  return tokensMulticall;
};

/**
 * We'll get the current block of a given RPC to see if we can query it
 * Without getting CORS error or any other error
 * @param rpc
 * @returns
 */
const testCosmosRpc = async (rpc: string): Promise<boolean> => {
  let rpcIsValid = false;
  try {
    const client = await StargateClient.connect(rpc);
    const block = await client.getBlock();
    rpcIsValid = !!block.id;
  } catch (error) {
    rpcIsValid = false;
  }
  return rpcIsValid;
};

export const getWorkingCosmosRpcUrl = async (
  chainData: ChainData
): Promise<string> => {
  const chain = chainData as CosmosChain;
  const rpcList =
    chain.rpcList.length > 0 ? chain.rpcList : chain.rpc ? [chain.rpc] : [];

  // if rpcList is empty, use the default rpc
  if (rpcList.length === 0) {
    try {
      const isValid = await testCosmosRpc(chain.rpc);
      if (isValid) {
        return chain.rpc;
      }
    } catch (error) {
      console.error(
        `Error fetching rpc url: ${chain.rpc} - Error: ${(error as any)?.toString()}`
      );
    }
  }

  // if rpcList is not empty, try to fetch each rpc until we find a valid one
  for (const rpc of rpcList) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const isValid = await timeout(5_000, testCosmosRpc(rpc));
      if (isValid) {
        return rpc;
      }
    } catch (error) {
      console.error(
        `Error fetching rpc url: ${chain.rpc} - Error: ${(error as any)?.toString()}`
      );
    }
  }

  // In the case that all rpcs are invalid, return the default rpc anyway
  return chain.rpc;
};

/**
 * Get the balance of a single token for a given address
 * @param chainRpc - rpc url
 * @param address - user address
 * @param tokenAddress - token address
 * @returns balance as string
 */
export const getCosmosTokenBalance = async (
  chain: ChainData,
  address: string,
  tokenAddress: string
): Promise<string> => {
  if (chain.chainType !== ChainType.COSMOS) return "0";

  const cosmosChain = chain as CosmosChain;
  const client = new CosmosRestClient(cosmosChain.rest);
  const balance = await client.getBalance(address, tokenAddress);

  return balance?.amount || "0";
};

/**
 * Fetches all Cosmos balances for a given address and RPC.
 *
 * @param {string} params.rpc - RPC URL to fetch balances from.
 * @param {string} params.address - user address to fetch balances for.
 * @param {Token[]} params.tokens - array of Cosmos tokens.
 * @returns {Promise<TokenWithBalance[]>} promise that resolves to an array of tokens with their balances.
 */
export const getAllCosmosBalancesForChain = async ({
  rest,
  address,
  tokens,
}: {
  rest: string;
  tokens: Token[];
  address: string;
}): Promise<TokenWithBalance[]> => {
  const client = new CosmosRestClient(rest);
  const cosmosBalances = await client.getAllBalances(address);

  // Need to map tokens and put balance, based on denom
  const tokensWithBalance = tokens.map((t) => {
    const balanceCoin = cosmosBalances.find(
      (c) => c.denom === normalizeIbcAddress(t.address)
    );
    return {
      ...t,
      balance: balanceCoin
        ? formatBNToReadable(balanceCoin?.amount, t?.decimals)
        : "0",
    };
  });

  return tokensWithBalance;
};

/**
 * Fetches all Cosmos balances for a given address and RPC.
 *
 * @param {object} params.addresses user addresses per cosmos network
 * @param {Token[]} params.cosmosTokens
 * @param {CosmosChain[]} params.cosmosChains
 * @returns {Promise<TokenWithBalance[]>} All tokens with their balances.
 */
export const getAllCosmosBalances = async ({
  addresses,
  cosmosTokens,
  cosmosChains,
}: {
  addresses: { chainId: string; address: string }[];
  cosmosTokens: Token[];
  cosmosChains: CosmosChain[];
}): Promise<TokenWithBalance[]> => {
  const tokensWithBalance: TokenWithBalance[] = [];

  await Promise.all(
    cosmosChains.map(async (cosmosChain) => {
      const userAddress = addresses.find(
        (a) => a.chainId === cosmosChain.chainId
      )?.address;
      const tokensForChain = cosmosTokens.filter(
        (t) => t.chainId === cosmosChain.chainId
      );

      if (!userAddress) {
        tokensWithBalance.push(
          ...tokensForChain.map((t) => ({ ...t, balance: "0" }))
        );
        return;
      }

      try {
        const balances = await timeout(
          6_000,
          getAllCosmosBalancesForChain({
            rest: cosmosChain.rest,
            address: userAddress,
            tokens: tokensForChain,
          })
        );

        if (balances) {
          tokensWithBalance.push(...balances);
        } else {
          tokensWithBalance.push(
            ...tokensForChain.map((t) => ({ ...t, balance: "0" }))
          );
        }
      } catch (error) {
        console.warn(
          `Failed to fetch balances for chain ${cosmosChain.chainId}:`,
          error
        );
        tokensWithBalance.push(
          ...tokensForChain.map((t) => ({ ...t, balance: "0" }))
        );
      }
    })
  );

  return tokensWithBalance;
};

export const getSolanaNativeBalance = async (
  userAddress: string
): Promise<bigint> => {
  const response = await fetch(SOLANA_RPC_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "getBalance",
      params: [userAddress],
    }),
  });
  const data = await response.json();
  return BigInt(data.result?.value || 0);
};

export const getSolanaTokenBalance = async (
  userAddress: string,
  tokenAddress: string
): Promise<bigint> => {
  if (tokenAddress.toLowerCase() === nativeSolanaTokenAddress.toLowerCase()) {
    return getSolanaNativeBalance(userAddress);
  }

  const balances = await getSolanaTokensBalance(userAddress, [tokenAddress]);
  return balances[tokenAddress] || BigInt(0);
};

const getSolanaTokenAccountsByProgram = async (
  userAddress: string,
  programId: string
): Promise<SolanaParsedTokenAccount[]> => {
  const response = await fetch(SOLANA_RPC_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "getTokenAccountsByOwner",
      params: [
        userAddress,
        { programId },
        { encoding: "jsonParsed" },
      ],
    }),
  });
  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message || "Failed to fetch Solana balances");
  }

  return data.result?.value || [];
};

export const getSolanaTokensBalance = async (
  userAddress: string,
  tokenAddresses: string[]
): Promise<{ [address: string]: bigint }> => {
  const balancesByAddress = tokenAddresses.reduce(
    (acc, address) => {
      acc[address] = BigInt(0);
      return acc;
    },
    {} as { [address: string]: bigint }
  );

  if (tokenAddresses.length === 0) {
    return balancesByAddress;
  }

  const addressByLowercase = tokenAddresses.reduce(
    (acc, address) => {
      acc[address.toLowerCase()] = address;
      return acc;
    },
    {} as { [address: string]: string }
  );

  const tokenAccountsByProgram = await Promise.all(
    [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID].map((programId) =>
      getSolanaTokenAccountsByProgram(userAddress, programId.toBase58())
    )
  );

  tokenAccountsByProgram.flat().forEach((tokenAccount) => {
    const tokenInfo = tokenAccount.account?.data?.parsed?.info;
    const address = tokenInfo?.mint
      ? addressByLowercase[tokenInfo.mint.toLowerCase()]
      : undefined;

    if (!address) return;

    balancesByAddress[address] += BigInt(tokenInfo?.tokenAmount?.amount || 0);
  });

  return balancesByAddress;
};

export const getAllSolanaTokensBalance = async (
  solanaTokens: Token[],
  userAddress: string
): Promise<TokenWithBalance[]> => {
  try {
    const nativeToken = solanaTokens.find(
      (t) =>
        t.address.toLowerCase() === nativeSolanaTokenAddress.toLowerCase()
    );
    const otherTokens = solanaTokens.filter(
      (t) =>
        t.address.toLowerCase() !== nativeSolanaTokenAddress.toLowerCase()
    );

    const [nativeBalance, tokenBalances] = await Promise.all([
      nativeToken
        ? getSolanaNativeBalance(userAddress)
        : Promise.resolve(BigInt(0)),
      getSolanaTokensBalance(
        userAddress,
        otherTokens.map((t) => t.address)
      ),
    ]);

    return solanaTokens.map((token) => ({
      ...token,
      balance:
        token.address.toLowerCase() === nativeSolanaTokenAddress.toLowerCase()
          ? formatBNToReadable(nativeBalance, token.decimals)
          : formatBNToReadable(tokenBalances[token.address], token.decimals),
    }));
  } catch (error) {
    console.warn("Failed to fetch Solana balances:", error);
    return solanaTokens.map((t) => ({ ...t, balance: "0" }));
  }
};

export async function getBitcoinNativeBalance(
  address: string
): Promise<string> {
  try {
    const isValidBitcoinAddress = isBitcoinAddressValid(address);

    if (!isValidBitcoinAddress) {
      throw new Error(`Invalid Bitcoin address: ${address}`);
    }

    const apiUrl = `https://blockchain.info/q/addressbalance/${address}?confirmations=6`;

    const response = await fetch(apiUrl);
    const balanceInSatoshis = await response.json();

    const isValidResponse =
      typeof balanceInSatoshis === "string" ||
      typeof balanceInSatoshis === "number";

    if (!isValidResponse) {
      throw new Error("Invalid balance response");
    }

    return String(balanceInSatoshis || "0");
  } catch (error) {
    console.error("Failed to fetch Bitcoin balance:", error);
    return "0";
  }
}

// TODO: implement fetching balances for all bitcoin tokens
export const getAllBitcoinTokensBalance = async (
  userAddress: string,
  bitcoinTokens: Token[]
): Promise<TokenWithBalance[]> => {
  const nativeBalance = await getBitcoinNativeBalance(userAddress);

  return Promise.all(
    bitcoinTokens.map(async (token) => {
      return {
        ...token,
        balance: formatBNToReadable(nativeBalance, token.decimals),
      };
    })
  );
};

/**
 * Returns the balance of a single Sui coin for a given address
 */
export const getSuiTokenBalance = async (
  userAddress: string,
  tokenAddress: string,
  rpcUrl: string
): Promise<bigint> => {
  const suiClient = new SuiRpcClient(rpcUrl);

  const { totalBalance } = await suiClient.getBalance(
    userAddress,
    tokenAddress
  );

  return BigInt(totalBalance || 0);
};

export const getAllSuiTokensBalance = async (
  userAddress: string,
  suiTokens: Token[],
  suiChains: ChainData[]
): Promise<TokenWithBalance[]> => {
  const getBalancesForChain = async (
    chain: ChainData
  ): Promise<TokenWithBalance[]> => {
    const tokensForChain = suiTokens.filter((t) => t.chainId === chain.chainId);

    const suiClient = new SuiRpcClient(chain.rpc);
    const foundBalances = await suiClient.getAllBalances(userAddress);

    const balanceMap = foundBalances.reduce(
      (acc, balance) => {
        acc[balance.coinType] = balance.totalBalance;
        return acc;
      },
      {} as Record<string, string>
    );

    return tokensForChain.map((token) => {
      const balanceBn = balanceMap[token.address] || "0";

      return {
        ...token,
        balance: formatBNToReadable(balanceBn, token.decimals),
      };
    });
  };

  const allResults: TokenWithBalance[][] = await Promise.all(
    suiChains.map(getBalancesForChain)
  );

  return allResults.flat();
};

export const getXrplTokenBalance = async (
  userAddress: string,
  token: Pick<Token, "decimals" | "address">,
  chain: ChainData
): Promise<bigint> => {
  const xrplClient = await getClient<ChainType.XRPL>(chain);

  const balance = await xrplClient.getBalance(userAddress, token.address);

  return parseToBigInt(balance, token.decimals);
};

export const getAllXrplTokensBalance = async (
  userAddress: string,
  xrplTokens: Token[],
  xrplChains: ChainData[]
): Promise<TokenWithBalance[]> => {
  const getBalancesForChain = async (
    chain: ChainData
  ): Promise<TokenWithBalance[]> => {
    const tokensForChain = xrplTokens.filter(
      (t) => t.chainId === chain.chainId
    );

    const xrplClient = await getClient<ChainType.XRPL>(chain);
    const foundBalances = await xrplClient.getAllBalances(userAddress);

    const balanceMap = foundBalances.reduce(
      (acc, { address, balance }) => {
        acc[address] = balance;
        return acc;
      },
      {} as Record<string, string>
    );

    return tokensForChain.map((token) => {
      return {
        ...token,
        balance: balanceMap[token.address] || "0",
      };
    });
  };

  const allResults: TokenWithBalance[][] = await Promise.all(
    xrplChains.map(getBalancesForChain)
  );

  return allResults.flat();
};

export const getStellarTokenBalance = async (
  userAddress: string,
  token: Token,
  chain: ChainData
): Promise<bigint> => {
  const stellarClient = await getClient<ChainType.STELLAR>(chain);

  if (!isStellarToken(token)) {
    throw new Error("Token must be a Stellar token");
  }

  const balance = await stellarClient.getBalance(
    userAddress,
    token.chainAssetConfig.stellar.contractAddress,
    chain.chainId
  );

  return BigInt(balance);
};

export const getAllStellarTokensBalance = async (
  userAddress: string,
  stellarTokens: Token[],
  stellarChains: ChainData[]
): Promise<TokenWithBalance[]> => {
  const getBalancesForChain = async (
    chain: ChainData
  ): Promise<TokenWithBalance[]> => {
    const tokensForChain = stellarTokens.filter(
      (t): t is StellarToken => t.chainId === chain.chainId && isStellarToken(t)
    );

    const stellarClient = await getClient<ChainType.STELLAR>(chain);
    const allBalances = await stellarClient.getAllBalances(
      userAddress,
      tokensForChain
    );

    return allBalances.map((token) => {
      return {
        ...token,
        balance: formatBNToReadable(token.balance, token.decimals),
      };
    });
  };

  const allResults: TokenWithBalance[][] = await Promise.all(
    stellarChains.map(getBalancesForChain)
  );

  return allResults.flat();
};

/**
 * Canton balances are private: they can only be read with a wallet-authorized
 * `ledgerApi` session (the connected wallet supplies a JWT that can read as the
 * party). So balance is fetchable only when a Canton wallet is connected, and
 * only for that wallet's party — there's no address-only read.
 */
export const getCantonTokenBalance = async (
  userAddress: string,
  token: Token,
  cantonConnector: CantonConnector
): Promise<string> => {
  if (!token.originalAddress) return "0";

  const instrument = parseCantonInstrument(token.originalAddress);
  if (!instrument) return "0";

  const ledgerEnd = await cantonConnector.ledgerApi<{
    offset: number;
  }>({ requestMethod: "get", resource: "/v2/state/ledger-end" });

  const activeContracts = await cantonConnector.ledgerApi({
    requestMethod: "post",
    resource: "/v2/state/active-contracts",
    body: buildHoldingsAcsRequestBody(userAddress, String(ledgerEnd.offset)),
  });

  const grouped = groupCantonHoldingBalances(activeContracts);
  return grouped[cantonInstrumentKey(instrument)] ?? "0";
};

/**
 * Fetch balances for all Canton tokens in one ACS query. Canton balances are
 * private, so this requires a connected wallet's authorized `ledgerApi`.
 */
export async function getAllCantonTokensBalance({
  connector,
  partyId,
  tokens,
}: {
  connector: CantonConnector;
  partyId: string;
  tokens: Token[];
}): Promise<TokenWithBalance[]> {
  const ledgerEnd = await connector.ledgerApi<{ offset: number }>({
    requestMethod: "get",
    resource: "/v2/state/ledger-end",
  });

  const activeContracts = await connector.ledgerApi({
    requestMethod: "post",
    resource: "/v2/state/active-contracts",
    body: buildHoldingsAcsRequestBody(partyId, String(ledgerEnd.offset)),
  });

  const grouped = groupCantonHoldingBalances(activeContracts);

  return tokens.map((token) => {
    const instrument = token.originalAddress
      ? parseCantonInstrument(token.originalAddress)
      : null;
    return {
      ...token,
      balance: instrument
        ? grouped[cantonInstrumentKey(instrument)] ?? "0"
        : "0",
    };
  });
}

/**
 * Returns a promise that resolves when the given promise resolves or after the given timeout
 * @param ms - timeout in milliseconds
 * @param promise - the promise to wait for
 */
export function timeout<T>(ms: number, promise: Promise<T>): Promise<T | null> {
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, ms);
  });

  return Promise.race([promise, timeoutPromise]);
}
